import apiClient, { buildQueryParams } from '../utils/apiClient';
import { handleError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';

/**
 * Service for Vehicle API calls
 */
const vehicleService = {
  /**
   * Get all vehicles with pagination and filters
   */
  getAllVehicles: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        search = '',
        status = '',
        isVerified,
        isFeatured,
        isActive,
        category,
        owner,
        city,
        startDate,
        endDate,
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = buildQueryParams({
        page,
        limit,
        search,
        status,
        isVerified,
        isFeatured,
        isActive,
        category,
        owner,
        city,
        startDate,
        endDate,
        sortBy,
        sortOrder,
      });

      return await apiClient.get(`${API_ENDPOINTS.VEHICLES.LIST}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.getAllVehicles'));
    }
  },

  /**
   * Get a single vehicle by ID
   */
  getVehicleById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.VEHICLES.GET_BY_ID(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.getVehicleById'));
    }
  },

  /**
   * Historique des modifications d'un véhicule (admin)
   */
  getVehicleHistory: async (id, params = {}) => {
    try {
      const queryParams = buildQueryParams({
        page: params.page || 1,
        limit: params.limit || 50,
      });
      return await apiClient.get(`${API_ENDPOINTS.VEHICLES.HISTORY(id)}?${queryParams}`);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.getVehicleHistory'));
    }
  },

  /**
   * Toggle vehicle active status
   */
  toggleActive: async (id) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.VEHICLES.TOGGLE_ACTIVE(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.toggleActive'));
    }
  },

  /**
   * Toggle vehicle verified status
   */
  toggleVerified: async (id) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.VEHICLES.TOGGLE_VERIFIED(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.toggleVerified'));
    }
  },

  /**
   * Toggle vehicle featured status
   */
  toggleFeatured: async (id) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.VEHICLES.TOGGLE_FEATURED(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.toggleFeatured'));
    }
  },

  /**
   * Delete a vehicle
   */
  deleteVehicle: async (id) => {
    try {
      return await apiClient.delete(API_ENDPOINTS.VEHICLES.DELETE(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.deleteVehicle'));
    }
  },

  /**
   * Reject a vehicle (Admin only)
   * @param {string} id - Vehicle ID
   * @param {string} message - Rejection message
   */
  rejectVehicle: async (id, message) => {
    try {
      return await apiClient.post(API_ENDPOINTS.ADMIN_VEHICLES.REJECT(id), {
        message,
      });
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.rejectVehicle'));
    }
  },

  /**
   * Reset vehicle rejection status (Admin only)
   * @param {string} id - Vehicle ID
   */
  resetVehicleRejection: async (id) => {
    try {
      return await apiClient.post(API_ENDPOINTS.ADMIN_VEHICLES.RESET_REJECTION(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.resetVehicleRejection'));
    }
  },

  /**
   * Block dates for a vehicle
   * @param {string} id - Vehicle ID
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   * @param {string} reason - Reason for blocking (e.g., 'Révision technique', 'Location hors-plateforme')
   */
  blockDates: async (id, startDate, endDate, reason) => {
    try {
      return await apiClient.post(API_ENDPOINTS.VEHICLES.BLOCK_DATES(id), {
        startDate,
        endDate,
        reason,
      });
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.blockDates'));
    }
  },

  /**
   * Get blocked dates for a vehicle
   * @param {string} id - Vehicle ID
   */
  getBlockedDates: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.VEHICLES.GET_BLOCKED_DATES(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.getBlockedDates'));
    }
  },

  /**
   * Delete blocked dates for a vehicle
   * URL: DELETE /api/vehicles/:id/block-dates/:periodId
   * @param {string} id - Vehicle ID
   * @param {string} blockedDateId - Blocked date ID (periodId) to delete
   */
  deleteBlockedDates: async (id, blockedDateId) => {
    try {
      // URL format: /vehicles/${vehicleId}/block-dates/${periodId}
      return await apiClient.delete(`${API_ENDPOINTS.VEHICLES.DELETE_BLOCKED_DATES(id)}/${blockedDateId}`);
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.deleteBlockedDates'));
    }
  },

  /**
   * Reject vehicle deletion request (Admin only)
   * @param {string} id - Vehicle ID
   */
  rejectVehicleDeletion: async (id) => {
    try {
      return await apiClient.patch(API_ENDPOINTS.ADMIN_VEHICLES.REJECT_DELETION(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.rejectVehicleDeletion'));
    }
  },

  /**
   * Delete vehicle permanently (Admin only)
   * @param {string} id - Vehicle ID
   */
  deleteVehiclePermanent: async (id) => {
    try {
      return await apiClient.delete(API_ENDPOINTS.ADMIN_VEHICLES.DELETE_PERMANENT(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.deleteVehiclePermanent'));
    }
  },

  /**
   * Save logs and delete vehicle permanently (Admin only)
   * @param {string} id - Vehicle ID
   */
  deleteVehicleWithLogs: async (id) => {
    try {
      return await apiClient.post(API_ENDPOINTS.ADMIN_VEHICLES.DELETE_WITH_LOGS(id));
    } catch (error) {
      throw new Error(handleError(error, 'vehicleService.deleteVehicleWithLogs'));
    }
  },
};

export default vehicleService;

